/**
 * importNotionSemantic — the Notion runtime consumer.
 *
 * Reads a Notion database semantically, builds the snapshot, and hands it to
 * the shared applySemanticImport path (items + identity + edges + Collection).
 * What this recovers over the flat import: relationships (relation properties),
 * category (option colors), and external identity.
 */

import { loadFieldMappingConfig, getColorRulesForEntity } from '../fieldMappingRegistry';
import { notionImportSemantic } from './notionSemanticApi';
import { buildNotionSnapshot } from './notionSnapshot';
import { applySemanticImport, type SemanticImportResult } from './applySemanticImport';

export type NotionImportResult = SemanticImportResult;

export async function importNotionDatabaseSemantic(params: {
  workspaceId: string;
  databaseId: string;
  userId?: string;
  groupIntoCollection?: boolean;
}): Promise<{ data: NotionImportResult | null; error: string | null }> {
  const { workspaceId, databaseId, userId, groupIntoCollection = true } = params;

  let read;
  try {
    read = await notionImportSemantic(workspaceId, databaseId, { userId });
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Notion read failed' };
  }

  const config = await loadFieldMappingConfig(workspaceId, 'notion');
  const snapshot = buildNotionSnapshot(read, {
    relationshipMap: config.relationshipMap,
    typeMap: config.typeMap,
    colorRules: getColorRulesForEntity(config.miroColorMaps ?? [], 'items'),
  });

  const externalUrlByExternalId = new Map(read.pages.map(p => [p.externalId, p.externalUrl]));
  const data = await applySemanticImport(snapshot, {
    workspaceId,
    connectorType: 'notion',
    sourceLabel: 'Notion import',
    sourceTag: 'notion-import',
    proposedByAgent: 'notion_import',
    containerTitle: read.database.title,
    externalUrlByExternalId,
    userId,
    groupIntoCollection,
  });
  return { data, error: null };
}
